import { z, type ZodObject } from 'zod';

import { ContactFormTrackingEventProperties } from './primitives';

/**
 * Segment recommends event names built from a noun and past-tense verb
 * @see https://segment.com/docs/connections/spec/track/#event
 *
 * DO NOT CHANGE THESE NAMES
 */
export const TRACKING_EVENT_NAMES = [
  /** When someone submits a step in a multi-step form */
  'Contact Form Step Submitted',
  /** When someone submits the contact form */
  'Contact Form Submitted',
  /** When someone starts the contact funnel from the homepage email capture */
  'Contact Form Submission Started',
  /** When someone subscribes to a newsletter */
  'Subscribed to Newsletter',
  /** When someone submits a community application */
  'Submitted Community Application',
  /** When a partner uses the referral form */
  'Partner Submitted Referral',
  /** When a user touches a cost calculator */
  'Used Calculator',
  // When the loyalty activation gap calculator page is viewed
  'Viewed Loyalty Activation Gap Calculator',
  // When the loyalty activation gap calculator form is submitted with invalid data
  'Loyalty Activation Gap Calculator Invalid Submission',
  // When the loyalty activation gap calculator produces results
  'Calculated Loyalty Activation Gap',
  // When a user chooses a calculator scenario
  'Selected Loyalty Activation Gap Scenario',
  // When a user returns to calculator inputs from results
  'Adjusted Loyalty Activation Gap Inputs',
  // When a user copies loyalty activation gap calculator results
  'Copied Loyalty Activation Gap Results',
  // When a user clicks the calculator assessment CTA
  'Clicked Loyalty Activation Gap Assessment CTA',
  // When a user clicks the announcement banner
  'Clicked Announcement',
  // When a user views a product
  'Viewed Product',
  // When a user views a resource
  'Viewed Resource',
  // When a user views an event
  'Viewed Event',
  // When a user views a blog post
  'Viewed Blog Post',
  // When a user views a case study
  'Viewed Case Study',
  // When a user views an integration
  'Viewed Integration',
  // When a user selects a search result
  'Search Result Selected',
  // When a prospect or customer books a meeting
  'Meeting Booked',
  // When a form enrichment is attempted
  'Form Enrichment Attempted',
  // When a user clicks an email link
  'Email Link Clicked',
  // When a user downloads gated content eg reports
  'Downloaded Gated Content',
  // When a contact form is processed via the backend cron job
  'Contact Form Submission Processed',
  // When a contact is created or updated in HubSpot (backend only). Properties are
  // the exact property map sent to HubSpot on the create/update request.
  'Hubspot Contact Create or Update',
  // Booking Modal Shown
  'Booking Modal Shown',
  // When a user reaches the end of the page (footer comes into view)
  'Reached End Of Page',
  // When a user lands on a 404 page
  '404 Triggered',
  // When a user clicks any link
  'Link Clicked',
  // When a user scrolls past 300px
  'Scrolled Past 300px',
  // When a user has been in a session for more than 5 seconds
  'Session Exceeded 5s',
] as const;
export const TrackingEvent = z.enum(TRACKING_EVENT_NAMES);
export type TrackingEvent = z.infer<typeof TrackingEvent>;

const MeetingSource = z.enum(['Chili Piper', 'Calendly']);

export const TrackingEventProperties = z.object({
  [TrackingEvent.enum['Contact Form Submitted']]:
    ContactFormTrackingEventProperties,
  [TrackingEvent.enum['Contact Form Submission Started']]:
    ContactFormTrackingEventProperties,
  [TrackingEvent.enum['Booking Modal Shown']]: z.object({
    email: z.string(),
    formId: z.string().optional(),
    submissionId: z.string().optional(),
    source: MeetingSource,
  }),
  [TrackingEvent.enum['Contact Form Submission Processed']]:
    ContactFormTrackingEventProperties,
  // The property map sent to HubSpot (HubSpot internal name -> string value).
  [TrackingEvent.enum['Hubspot Contact Create or Update']]: z.record(
    z.string(),
    z.string(),
  ),
  [TrackingEvent.enum['Contact Form Step Submitted']]: z.object({
    step: z.number(),
    totalSteps: z.number(),
    isFinalStep: z.boolean(),
    formId: z.string(),
    submissionId: z.string(),
  }),
  [TrackingEvent.enum['Form Enrichment Attempted']]: z.object({
    email: z.string(),
    wasSuccessful: z.boolean(),
    formId: z.string(),
    submissionId: z.string(),
  }),
  [TrackingEvent.enum['Subscribed to Newsletter']]:
    ContactFormTrackingEventProperties,
  [TrackingEvent.enum['Submitted Community Application']]:
    ContactFormTrackingEventProperties,
  [TrackingEvent.enum['Partner Submitted Referral']]:
    ContactFormTrackingEventProperties,
  [TrackingEvent.enum['Used Calculator']]: z.object({
    resourceTitle: z.string(),
    resourceId: z.string(),
  }),
  [TrackingEvent.enum['Viewed Loyalty Activation Gap Calculator']]: z
    .object({})
    .optional(),
  [TrackingEvent.enum['Loyalty Activation Gap Calculator Invalid Submission']]:
    z.object({
      reason: z.string(),
    }),
  [TrackingEvent.enum['Calculated Loyalty Activation Gap']]: z.object({
    members: z.number(),
    activationRate: z.number(),
    industry: z.string(),
    brands: z.number(),
    systems: z.number(),
    rpm: z.number(),
    potential: z.number(),
    recoveryMax: z.number(),
    selectedScenario: z.string(),
  }),
  [TrackingEvent.enum['Selected Loyalty Activation Gap Scenario']]: z.object({
    scenario: z.string(),
    scenarioName: z.string(),
    recovery: z.number(),
  }),
  [TrackingEvent.enum['Adjusted Loyalty Activation Gap Inputs']]: z
    .object({})
    .optional(),
  [TrackingEvent.enum['Copied Loyalty Activation Gap Results']]: z.object({
    scenario: z.string(),
    recovery: z.number(),
  }),
  [TrackingEvent.enum['Clicked Loyalty Activation Gap Assessment CTA']]:
    z.object({
      location: z.string(),
      scenario: z.string(),
      recovery: z.number(),
    }),
  [TrackingEvent.enum['Clicked Announcement']]: z.object({
    message: z.string(),
    href: z.string(),
  }),
  [TrackingEvent.enum['Viewed Product']]: z.object({
    productName: z.string(),
  }),
  [TrackingEvent.enum['Viewed Resource']]: z.object({
    resourceTitle: z.string(),
    resourceId: z.string(),
  }),
  [TrackingEvent.enum['Viewed Event']]: z.object({
    eventTitle: z.string(),
    eventId: z.string(),
  }),
  [TrackingEvent.enum['Viewed Blog Post']]: z.object({
    blogPostTitle: z.string(),
    blogPostCategories: z.array(z.string()),
    blogPostId: z.string(),
  }),
  [TrackingEvent.enum['Viewed Case Study']]: z.object({
    caseStudyTitle: z.string(),
    caseStudyCategories: z.array(z.string()),
    caseStudyId: z.string(),
  }),
  [TrackingEvent.enum['Viewed Integration']]: z.object({
    integrationName: z.string(),
    integrationSlug: z.string(),
    integrationId: z.string(),
  }),
  [TrackingEvent.enum['Search Result Selected']]: z.object({
    /** The Algolia index name for this search */
    algoliaIndexName: z.string().min(1),
    /** The path slug of the hit */
    hitSlug: z.string().min(1),
    /** The title of the hit */
    hitTitle: z.string().min(1),
    /** The Algolia object ID */
    hitObjectID: z.string().min(1),
    /** The types of search results */
    hitType: z.string().min(1),
  }),
  [TrackingEvent.enum['Meeting Booked']]: z.object({
    inviteeEmail: z.string(),
    inviteeName: z.string(),
    inviteeCompany: z.string().optional(),
    eventName: z.string(),
    eventType: z.string(),
    eventDescription: z.string().optional(),
    eventAssignedToEmail: z.string(),
    eventStartTime: z.iso.datetime(),
    eventID: z.string(),
    source: MeetingSource,
  }),
  [TrackingEvent.enum['Email Link Clicked']]: z.object({
    email: z.email(),
    title: z.string(),
    pathname: z.string(),
  }),
  [TrackingEvent.enum['Downloaded Gated Content']]:
    ContactFormTrackingEventProperties.unwrap().extend({
      contentTitle: z.string().optional(),
    }),
  [TrackingEvent.enum['Reached End Of Page']]: z.object({}).optional(),
  [TrackingEvent.enum['404 Triggered']]: z.object({
    path: z.string(),
  }),
  [TrackingEvent.enum['Link Clicked']]: z.object({
    href: z.string(),
    text: z.string().optional(),
    isExternal: z.boolean(),
  }),
  [TrackingEvent.enum['Scrolled Past 300px']]: z.object({}).optional(),
  [TrackingEvent.enum['Session Exceeded 5s']]: z.object({}).optional(),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}) satisfies ZodObject<Record<keyof typeof TrackingEvent.enum, any>>;
export type TrackingEventProperties = z.infer<typeof TrackingEventProperties>;
